import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Hub } from 'aws-amplify/utils';
import { fetchAuthSession, fetchUserAttributes, getCurrentUser } from 'aws-amplify/auth';
import {
  hasPermission,
  primaryRole,
  rolesFromGroups,
  type Permission,
  type Role,
} from '@/util/permissions';

/**
 * Cognito session for the Contracting dashboard.
 *
 * Same user pool as the finance dashboard — one identity across ROCA. What
 * differs is the roles this app cares about: ADMIN, CONTRACTING,
 * BROKER_SUPPORT and ONBOARDING, which gate both the nav and the API.
 */

export interface AuthUser {
  userId: string;
  username: string;
  email?: string;
  name?: string;
  groups: string[];
}

type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  user: AuthUser | null;
  status: AuthStatus;
  isLoading: boolean;
  isAuthenticated: boolean;
  roles: Role[];
  /** ADMIN wins when the user is in several groups. */
  role: Role | null;
  /**
   * Whether the current role may perform an action. Presentation only — the
   * server enforces the same map in middleware/require-role.ts.
   */
  can: (permission: Permission) => boolean;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [status, setStatus] = useState<AuthStatus>('loading');

  const fetchUser = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      if (!session.tokens) throw new Error('No session');

      const [currentUser, attributes] = await Promise.all([
        getCurrentUser(),
        fetchUserAttributes(),
      ]);

      const groups =
        (session.tokens.accessToken.payload['cognito:groups'] as
          | string[]
          | undefined) ?? [];

      setUser({
        userId: currentUser.userId,
        username: currentUser.username,
        email: attributes.email,
        name: [attributes.given_name, attributes.family_name]
          .filter(Boolean)
          .join(' '),
        groups,
      });
      setStatus('authenticated');
    } catch {
      setUser(null);
      setStatus('unauthenticated');
    }
  }, []);

  useEffect(() => {
    // fetchUser awaits Cognito before it touches state, so this is not the
    // synchronous cascade the rule guards against — it is exactly the
    // "subscribe to an external system" case, with the initial read up front.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchUser();

    const cancel = Hub.listen('auth', async ({ payload }) => {
      if (payload.event === 'signedIn') {
        await fetchUser();
      } else if (
        payload.event === 'signedOut' ||
        payload.event === 'tokenRefresh_failure'
      ) {
        setUser(null);
        setStatus('unauthenticated');
      }
    });

    return () => cancel();
  }, [fetchUser]);

  const roles = useMemo(() => rolesFromGroups(user?.groups), [user]);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      status,
      isLoading: status === 'loading',
      isAuthenticated: status === 'authenticated',
      roles,
      role: primaryRole(roles),
      can: (permission: Permission) => hasPermission(roles, permission),
      refreshUser: fetchUser,
    }),
    [user, status, roles, fetchUser],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
